import * as React from 'react';
import { toast } from 'react-toastify';
import { EpaQuestions } from './models/JobInterfaces';
import { GenericList } from './models/JobSharedInterfaces';

interface JobQuoteEpaQuestionsProps {
    isReadOnly: boolean;
    invoiceDescription: string;
    includeGreaseTrap: boolean;
    showInvoiceDescription: boolean;
    itemTypeId: number;
    epaLidLocationFeeFormatted: string;
    weekendFeeFormatted: string;
    emergencyFeeFormatted: string;
    epaQuestions: EpaQuestions;
    yesNoOptions: GenericList[];
    showBack: boolean;
    saveDisabled: boolean;
    updateText(fieldName: string, fieldValue: string): void;
    updateNumber(fieldName: string, fieldValue: number): void;
    updateInvoiceDescription(invDescription: string): void;
    updateIncGreaseTrap(incGreaseTrap: boolean): void;
    updateSaveDisabled(saveDisabled: boolean): void;
    save(): void;
}


const JobQuoteEpaQuestionsData = (props: JobQuoteEpaQuestionsProps) => {
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});

    const clearError = (errorName: string) => {
        var errorList = errors;
        if (errorName) {
            delete errorList[errorName];
            setErrors(errorList);
        }
    }

    const handleEpaQuestionChange = (e: any) => {
        clearError(e.target.name);
        var value = e.target.value * 1;

        props.updateNumber(e.target.name, value);
    }

    const handleEpaQuestionTextChange = (e: any) => {
        clearError(e.target.name);
        props.updateText(e.target.name, e.target.value);
    }

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;


        if (props.itemTypeId === 58) {
            //only need to answer these questions for septic tanks

            if (props.epaQuestions.tankLidExposed < 0) {
                formIsValid = false;
                errorList["tankLidExposed"] = "Lid of Tank Exposed is required";
            }

            if (props.epaQuestions.tankLidExposed === 0 && props.epaQuestions.tankLidLocationKnown < 0) {
                formIsValid = false;
                errorList["tankLidLocationKnown"] = "Where tank lid is located is required";
            }

            if (props.epaQuestions.tankLidLocationKnown === 1 && props.epaQuestions.tankLidLocation == "") {
                formIsValid = false;
                errorList["tankLidLocation"] = "Tank lid is located is required";
            }

            if (props.epaQuestions.truckLargeAccess < 0) {
                formIsValid = false;
                errorList["truckLargeAccess"] = "Access for large truck is required";
            }

            if (props.epaQuestions.truckLargeAccess === 0 && props.epaQuestions.truckSmallAccess < 0) {
                formIsValid = false;
                errorList["truckSmallAccess"] = "Access for small truck is required";
            }

            if (props.epaQuestions.truckAccessToTank < 0) {
                formIsValid = false;
                errorList["truckAccessToTank"] = "Truck access within 25m is required";
            }

            if (props.epaQuestions.steepDriveway < 0) {
                formIsValid = false;
                errorList["steepDriveway"] = "Steep driveway is required";
            }

            if ((props.epaQuestions.truckAccessToTank === 0 || props.epaQuestions.steepDriveway === 1) && props.epaQuestions.hoseMetres <= 0) {
                formIsValid = false;
                errorList["hoseMetres"] = "# Hose Metres is required";
            }

            if (props.epaQuestions.sealedDriveway < 0) {
                formIsValid = false;
                errorList["sealedDriveway"] = "Sealed driveway is required";
            }
        }

        if (props.epaQuestions.waterAccessOnSite < 0) {
            formIsValid = false;
            errorList["waterAccessOnSite"] = "Water Access On Site is required";
        }

        if (props.itemTypeId > 57 && props.itemTypeId < 61) {
            if (props.epaQuestions.lastService === "") {
                formIsValid = false;
                errorList["lastService"] = "Last Service is required";
            }
        }

        if (props.itemTypeId === 61) {
            if (props.epaQuestions.siteVisit < 0) {
                formIsValid = false;
                errorList["siteVisit"] = "Site Visit is required";
            }
            if (props.epaQuestions.confinedSpace < 0) {
                formIsValid = false;
                errorList["confinedSpace"] = "Confined Space is required";
            }
            if (props.epaQuestions.materialSafetyDataSheet < 0) {
                formIsValid = false;
                errorList["materialSafetyDataSheet"] = "MSDS is required";
            }
            if (props.epaQuestions.materialSafetyDataSheet === 0 && props.epaQuestions.sampleTestingRequired < 0) {
                formIsValid = false;
                errorList["sampleTestingRequired"] = "Sample Testing is required";
            }
        }

        if (props.epaQuestions.weekendJob < 0) {
            formIsValid = false;
            errorList["weekendJob"] = "Weekend job is required";
        }

        if (props.epaQuestions.weekendJob === 0 && props.epaQuestions.emergencySameDayJob < 0) {
            formIsValid = false;
            errorList["emergencySameDayJob"] = "Emergency / Same Day job is required";
        }

        setErrors(errorList);
        return formIsValid;
    }

    const save = () => {
        if (validate()) {
            if (props.saveDisabled) {
                return;
            }
            props.updateSaveDisabled(true);
            props.save();
        } else {
            props.updateSaveDisabled(false);
            toast.error("Please fix the errors on screen before saving!");
        }
    }

    return (
        <div>
            <div className={props.showInvoiceDescription ? "" : "hidden" }>
                <label className="input-group marginBottom10" htmlFor="invoiceDescription">
                    <span className="label-xlarge">Invoice Description</span>
                    <input className="input marginRight10" type="text" maxLength={200} id="invoiceDescription" name="invoiceDescription" value={props.invoiceDescription} onChange={(e) => props.updateInvoiceDescription(e.target.value)}></input>
                </label>
            </div>
            <div className={props.itemTypeId === 58 ? "" : "hidden"}>
                <label className={props.showInvoiceDescription ? "input-group marginBottom10" : "hidden"} htmlFor="includeGreaseTrap">
                    <span className="label-xlarge">Include Grease Trap?</span>
                    <input className="checkbox" type="checkbox" id="includeGreaseTrap" name="includeGreaseTrap" checked={props.includeGreaseTrap} onChange={(e) => props.updateIncGreaseTrap(e.target.checked)}></input>
                </label>

                <label className="input-group marginBottom10" htmlFor="tankLidExposed">
                    <span className="label-xlarge">Is the lid of the tank exposed?</span>
                    <select className="select" id="tankLidExposed" name="tankLidExposed" value={props.epaQuestions.tankLidExposed} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["tankLidExposed"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["tankLidExposed"]}</span>
                <div className={props.epaQuestions.tankLidExposed === 1 ? "marginLeftLabelxLarge makeitbold makeitred" : "hidden"}>Ask customer to email a photo of the location of the lid</div>

                <label className={props.epaQuestions.tankLidExposed === 0 ? "input-group marginBottom10" : "hidden"} htmlFor="tankLidLocationKnown">
                    <span className="label-xlarge">Do you know where the tank lid is located?</span>
                    <select className="select" id="tankLidLocationKnown" name="tankLidLocationKnown" value={props.epaQuestions.tankLidLocationKnown} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["tankLidLocationKnown"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["tankLidLocationKnown"]}</span>

                <label className={props.epaQuestions.tankLidLocationKnown === 1 ? "input-group marginBottom10" : "hidden"} htmlFor="tankLidLocation">
                    <span className="label label-xlarge">Location of Lid</span>
                    <input className="input" type="text" id="tankLidLocation" name="tankLidLocation" value={props.epaQuestions.tankLidLocation} onChange={(e) => handleEpaQuestionTextChange(e)}></input>
                </label>
                <span className={errors["tankLidLocation"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["tankLidLocation"]}</span>
                <div className={props.epaQuestions.tankLidExposed === 0 ? "marginLeftLabelxLarge makeitbold makeitred" : "hidden"}>Let the customer know there is a {props.epaLidLocationFeeFormatted} extra charge per 30 mins to locate the lid of the tank</div>

                <label className="input-group marginBottom10" htmlFor="truckLargeAccess">
                    <span className="label-xlarge">Is there access for a large truck?<br></br>(3.5h x 1.8w x 6.2l)</span>
                    <select className="select" id="truckLargeAccess" name="truckLargeAccess" value={props.epaQuestions.truckLargeAccess} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["truckLargeAccess"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["truckLargeAccess"]}</span>

                <label className={props.epaQuestions.truckLargeAccess === 0 ? "input-group marginBottom10" : "hidden"} htmlFor="truckSmallAccess">
                    <span className="label-xlarge">Is there access for a small truck?<br></br>(2.4h x 1.8w x 3.6l)</span>
                    <select className="select" id="truckSmallAccess" name="truckSmallAccess" value={props.epaQuestions.truckSmallAccess} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["truckSmallAccess"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["truckSmallAccess"]}</span>

                <label className="input-group marginBottom10" htmlFor="truckAccessToTank">
                    <span className="label-xlarge">Can a truck get within 25 metres of the septic tank?</span>
                    <select className="select" id="truckAccessToTank" name="truckAccessToTank" value={props.epaQuestions.truckAccessToTank} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["truckAccessToTank"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["truckAccessToTank"]}</span>

                <label className="input-group marginBottom10" htmlFor="steepDriveway">
                    <span className="label-xlarge">Is the driveway steep?</span>
                    <select className="select" id="steepDriveway" name="steepDriveway" value={props.epaQuestions.steepDriveway} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["steepDriveway"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["steepDriveway"]}</span>

                <label className={props.epaQuestions.truckAccessToTank === 0 || props.epaQuestions.steepDriveway === 1 ? "input-group" : "hidden"} htmlFor="hoseMetres">
                    <span className="label label-xlarge">Enter # metres of Hose required</span>
                    <input className="input customer__invDays" type="number" min="0" step="1" id="hoseMetres" name="hoseMetres" value={props.epaQuestions.hoseMetres} onChange={(e) => handleEpaQuestionChange(e)}></input>
                </label>
                <span className={errors["hoseMetres"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["hoseMetres"]}</span>

                <label className="input-group marginBottom10" htmlFor="sealedDriveway">
                    <span className="label-xlarge">Is the driveway sealed?</span>
                    <select className="select" id="sealedDriveway" name="sealedDriveway" value={props.epaQuestions.sealedDriveway} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["sealedDriveway"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["sealedDriveway"]}</span>
            </div>

            <div className={props.itemTypeId === 59 ? "" : "hidden"}>
                <label className="input-group" htmlFor="waterBoard">
                    <span className="label label-xlarge">Water Board that manages Grease Trap</span>
                    <input className="input" type="text" id="waterBoard" name="waterBoard" value={props.epaQuestions.waterBoard} onChange={(e) => handleEpaQuestionTextChange(e)}></input>
                </label>
                <span className={errors["waterBoard"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["waterBoard"]}</span>
            </div>

            <label className="input-group marginBottom10" htmlFor="waterAccessOnSite">
                <span className="label-xlarge">Is there access to water on site via a garden tap?</span>
                <select className="select" id="waterAccessOnSite" name="waterAccessOnSite" value={props.epaQuestions.waterAccessOnSite} onChange={(e) => handleEpaQuestionChange(e)}>
                    <option hidden defaultValue="-1"></option>
                    {props.yesNoOptions.map(yn =>
                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                    )};
                </select>
            </label>
            <span className={errors["waterAccessOnSite"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["waterAccessOnSite"]}</span>

            <div className={props.itemTypeId > 57 && props.itemTypeId < 61 ? "" : "hidden" }>
                <label className="input-group" htmlFor="lastService">
                    <span className="label label-xlarge">When was the Last Service (# months/years)</span>
                    <input className="input" type="text" id="lastService" name="lastService" value={props.epaQuestions.lastService} onChange={(e) => handleEpaQuestionTextChange(e)}></input>
                </label>
                <span className={errors["lastService"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["lastService"]}</span>
                <div className="marginLeftLabelxLarge makeitbold makeitred marginBottom10">Let the customer know there may be extra charges if any blockages</div>
            </div>

            <div className={props.itemTypeId === 61 ? "" : "hidden"}>
                <label className="input-group marginBottom10" htmlFor="siteVisit">
                    <span className="label-xlarge">Do you require a Site Visit?</span>
                    <select className="select" id="siteVisit" name="siteVisit" value={props.epaQuestions.siteVisit} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["siteVisit"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["siteVisit"]}</span>

                <label className="input-group marginBottom10" htmlFor="confinedSpace">
                    <span className="label-xlarge">Is there a confined space?</span>
                    <select className="select" id="confinedSpace" name="confinedSpace" value={props.epaQuestions.confinedSpace} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["confinedSpace"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["confinedSpace"]}</span>

                <label className="input-group marginBottom10" htmlFor="materialSafetyDataSheet">
                    <span className="label-xlarge">Do you have a Material Safety Data Sheet (MSDS)?</span>
                    <select className="select" id="materialSafetyDataSheet" name="materialSafetyDataSheet" value={props.epaQuestions.materialSafetyDataSheet} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["materialSafetyDataSheet"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["materialSafetyDataSheet"]}</span>
                <div className={props.epaQuestions.materialSafetyDataSheet === 1 ? "marginLeftLabelxLarge makeitbold makeitred" : ""}>Ask the customer to email the MSDS</div>

                <label className={props.epaQuestions.materialSafetyDataSheet === 0 ? "input-group marginBottom10" : "hidden"} htmlFor="sampleTestingRequired">
                    <span className="label-xlarge">Is Sample Testing Reqired?</span>
                    <select className="select" id="sampleTestingRequired" name="sampleTestingRequired" value={props.epaQuestions.sampleTestingRequired} onChange={(e) => handleEpaQuestionChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {props.yesNoOptions.map(yn =>
                            <option key={yn.id} value={yn.id}>{yn.name}</option>
                        )};
                    </select>
                </label>
                <span className={errors["sampleTestingRequired"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["sampleTestingRequired"]}</span>

                <div className="marginLeftLabelxLarge makeitbold makeitred">Let the customer know there will be extra charges for {props.epaQuestions.materialSafetyDataSheet === 1 ? "" : "sample testing and"} waste disposal</div>
            </div>

            <label className="input-group marginBottom10" htmlFor="weekendJob">
                <span className="label-xlarge">Is this a weekend, public holiday, or after 5PM job?</span>
                <select className="select" id="weekendJob" name="weekendJob" value={props.epaQuestions.weekendJob} onChange={(e) => handleEpaQuestionChange(e)}>
                    <option hidden defaultValue="-1"></option>
                    {props.yesNoOptions.map(yn =>
                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                    )};
                </select>
            </label>

            <span className={errors["weekendJob"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["weekendJob"]}</span>
            <div className={props.epaQuestions.weekendJob === 1 ? "marginLeftLabelxLarge makeitbold makeitred" : "hidden"}>Let the customer know there is a {props.weekendFeeFormatted} fee for weekend jobs.</div>

            <label className={props.epaQuestions.weekendJob === 0 ? "input-group marginBottom10" : "hidden"} htmlFor="emergencySameDay">
                <span className="label-xlarge">Is this an emergency / same day job?</span>
                <select className="select" id="emergencySameDayJob" name="emergencySameDayJob" value={props.epaQuestions.emergencySameDayJob} onChange={(e) => handleEpaQuestionChange(e)}>
                    <option hidden defaultValue="-1"></option>
                    {props.yesNoOptions.map(yn =>
                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                    )};
                </select>
            </label>
            <span className={errors["emergencySameDayJob"] ? "label errors marginLeftLabelxLarge" : "hidden"}>{errors["emergencySameDayJob"]}</span>
            <div className={props.epaQuestions.emergencySameDayJob === 1 ? "marginLeftLabelxLarge makeitbold makeitred" : "hidden"}>Let the customer know there is a {props.emergencyFeeFormatted} fee for an emergency / same day job</div>
            <hr></hr>
            <div className={props.showBack ? "makeitflexspacebetween" : "makeitflexright"}>
                <button className={props.isReadOnly || !props.showBack ? "hidden" : "popup-button--no"} onClick={props.save}>Back</button>
                <button className={props.isReadOnly ? "hidden" : "popup-button--yes"} onClick={save}>Save</button>
            </div>
        </div>
    )

}

export default JobQuoteEpaQuestionsData;